"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SlidersHorizontal as Sliders, ShieldAlert, Cpu, Wrench, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function Controller() {
  const shouldReduceMotion = useReducedMotion();
  const animationVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 1, 0.5, 1]
      }
    }
  };
  return <section id="comparemechanicalbullcontroller" data-nav="light" className="bg-background text-foreground py-16 px-4 md:px-8 border-b border-border">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={animationVariants} className="border border-border bg-card shadow-xs">
          {/* Industrial Spec Header Bar */}
          <div className="border-b border-border bg-muted/60 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="inline-block size-2 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                EQUIPMENT ADVISORY // SEC-CTRL-01
              </span>
            </div>
            <Badge variant="outline" className="font-mono text-[11px] uppercase tracking-wider rounded-none border-border bg-background px-2.5 py-0.5">
              TECHNICAL EVALUATION
            </Badge>
          </div>

          <div className="p-6 md:p-10 space-y-8">
            {/* Heading and Primary Copy Card */}
            <div>
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Compare the Mechanical Bull Controller
              </h2>
              <Separator className="my-5 bg-border" />
              <p className="text-base md:text-lg leading-relaxed text-foreground">
                The controller affects how the operator runs the machine, so it deserves more attention than it often receives. Different systems may provide different combinations of manual control, programmed modes, speed adjustment and motion control.
              </p>
            </div>

            {/* Grid of Advisory Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Due Diligence Card */}
              <Card className="rounded-none border border-border bg-background shadow-none p-0">
                <CardHeader className="border-b border-border bg-muted/40 p-5 flex flex-row items-center gap-3">
                  <div className="p-2 border border-border bg-card text-foreground">
                    <Wrench className="size-4 text-primary" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block">
                      VERIFICATION CHECKLIST
                    </span>
                    <CardTitle className="text-sm font-semibold tracking-normal text-foreground uppercase">
                      Operational Due Diligence
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-5">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Before buying, identify the controller supplied with the exact machine and review the operating information provided for it. For used equipment, also check its visible condition, known repairs and whether replacement components remain available.
                  </p>
                </CardContent>
              </Card>

              {/* Hardware Overview Indicator Card */}
              <Card className="rounded-none border border-border bg-background shadow-none p-0">
                <CardHeader className="border-b border-border bg-muted/40 p-5 flex flex-row items-center gap-3">
                  <div className="p-2 border border-border bg-card text-foreground">
                    <Cpu className="size-4 text-primary" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block">
                      SUBSYSTEM ARCHITECTURE
                    </span>
                    <CardTitle className="text-sm font-semibold tracking-normal text-foreground uppercase">
                      Console Integration
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-5">
                  <div className="space-y-3 font-mono text-xs">
                    <div className="flex items-center justify-between border-b border-border/60 pb-2">
                      <span className="text-muted-foreground">INPUT INTERFACE</span>
                      <span className="font-medium text-foreground">Manual Joystick / Dial</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border/60 pb-2">
                      <span className="text-muted-foreground">AUTOMATION</span>
                      <span className="font-medium text-foreground">Preset / Custom Programs</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">SAFETY KILL-SWITCH</span>
                      <span className="font-medium text-foreground">Dedicated Circuit Stop</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Compatibility Alert Plate */}
            <Alert variant="default" className="rounded-none border-l-4 border-l-destructive border-t-border border-r-border border-b-border bg-muted/30 p-5">
              <ShieldAlert className="size-5 text-destructive translate-y-0" />
              <div>
                <AlertTitle className="font-mono text-xs uppercase tracking-wider font-semibold text-foreground mb-1">
                  CRITICAL COMPATIBILITY REQUIREMENT
                </AlertTitle>
                <AlertDescription className="text-sm text-foreground leading-relaxed">
                  Do not assume that another control box can replace the original simply because it looks similar. Compatibility should be confirmed before substitution.
                </AlertDescription>
              </div>
            </Alert>

            {/* Bottom Internal Navigation Callout */}
            <div className="border border-border bg-muted/40 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center gap-3">
                <Sliders className="size-4 text-primary shrink-0 mt-0.5 sm:mt-0" />
                <span className="text-sm text-foreground">
                  Looking for a replacement controller? Browse{" "}
                  <Link to="/MechanicalBullPartsAndAccessoriesPage" className="font-semibold underline underline-offset-4 text-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
                    Mechanical Bull Parts &amp; Accessories
                    <ArrowRight className="size-3.5" />
                  </Link>
                </span>
              </div>
              <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                REF: OEM-CATALOG
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}